/**
 * Upload Service — S3 direct upload via pre-signed URLs
 */

import { api } from './api';

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Upload a file to S3 via pre-signed URL with progress tracking.
 *
 * 1. POST /api/uploads/presign → { uploadUrl, fileId }
 * 2. XHR PUT to S3 uploadUrl (for progress tracking)
 * 3. POST /api/uploads/complete → triggers processing
 */
export async function uploadToS3(
  file: File,
  projectId: string,
  assetType: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<{ fileId: string; s3Key: string }> {
  // 1. Get pre-signed URL
  const { uploadUrl, fileId, s3Key } = await api.upload.presign({
    projectId,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    assetType,
  });

  // 2. Upload directly to S3 with XHR for progress
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error('S3 upload failed: network error'));
    xhr.onabort = () => reject(new Error('S3 upload aborted'));

    xhr.send(file);
  });

  // 3. Confirm upload complete
  await api.upload.complete(fileId);

  return { fileId, s3Key };
}

/**
 * Abort an in-progress upload
 */
export async function abortUpload(fileId: string): Promise<void> {
  await api.upload.abort(fileId);
}
